import Dexie, { type Table } from "dexie";
import type { BaseEntry, PluginDatabase, PluginDatabaseService } from "@saludio/core";

class PluginDexieDB<EntryType extends BaseEntry> extends Dexie {
  entries!: Table<EntryType, string>;

  constructor(pluginId: string) {
    super(`${pluginId}-db`);
    this.version(1).stores({
      entries: "id, createdOn",
    });
  }
}

class PluginDatabaseImpl<EntryType extends BaseEntry> implements PluginDatabase<EntryType> {
  private dexieDb: PluginDexieDB<EntryType>;

  constructor(pluginId: string) {
    this.dexieDb = new PluginDexieDB<EntryType>(pluginId);
  }

  async saveEntry(entry: Omit<EntryType, keyof BaseEntry> | EntryType): Promise<EntryType> {
    const newEntry: EntryType =
      "id" in entry && "createdOn" in entry
        ? (entry as EntryType)
        : ({
            ...entry,
            id: crypto.randomUUID(),
            createdOn: new Date().toISOString(),
          } as EntryType);

    await this.dexieDb.entries.put(newEntry);
    return newEntry;
  }

  getAllEntries(): Promise<Array<EntryType>> {
    return this.dexieDb.entries.toArray();
  }

  async getEntryById(id: string): Promise<EntryType | undefined> {
    return this.dexieDb.entries.get(id) as Promise<EntryType | undefined>;
  }

  async getLastEntry(): Promise<EntryType | null> {
    const entry = await this.dexieDb.entries.orderBy("createdOn").last();
    return (entry as EntryType) ?? null;
  }

  getLastEntries(count: number): Promise<Array<EntryType>> {
    return this.dexieDb.entries.orderBy("createdOn").reverse().limit(count).toArray();
  }

  async clear(): Promise<void> {
    await this.dexieDb.entries.clear();
  }
}

export class DatabaseService {
  private static pluginDatabaseServices = new Map<string, PluginDatabaseService>();

  static forPlugin(pluginId: string): PluginDatabaseService {
    let service = this.pluginDatabaseServices.get(pluginId);
    if (!service) {
      service = {
        getDatabase<EntryType extends BaseEntry>(): PluginDatabase<EntryType> {
          return new PluginDatabaseImpl<EntryType>(pluginId);
        },
      };
      this.pluginDatabaseServices.set(pluginId, service);
    }
    return service;
  }
}
